import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, gbp2 } from "@/lib/api";

// Internal DB statuses (left) → display labels shown to the admin (right).
// Kept as-is in the database so we never break existing rows.
const STATUS_LABELS = {
  Active: "AVAILABLE",
  Redeemed: "USED",
  Refunded: "REFUNDED",
  Voided: "REFUNDED",       // consultation refunded pre-attendance
  Retained: "EXPIRED",      // no-show / late-cancel — £50 kept, credit dead
  "No-Show": "EXPIRED",
};
const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Redeemed: "bg-stone-100 text-stone-600 border-stone-300",
  Refunded: "bg-sky-50 text-sky-700 border-sky-200",
  Voided: "bg-sky-50 text-sky-700 border-sky-200",
  Retained: "bg-amber-50 text-amber-800 border-amber-200",
  "No-Show": "bg-red-50 text-red-700 border-red-200",
};

export default function AdminConsultationCredits() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCredit, setOpenCredit] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/consultation-credits"); setRows(r.data || []); }
    catch { toast.error("Could not load credits"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="consultation-credits-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-4xl font-light">Consultation Credits</h2>
        <button onClick={load} data-testid="consultation-credits-refresh" className="mono-label border border-stone-900 px-4 py-2 hover:bg-stone-900 hover:text-white transition">Refresh</button>
      </div>
      <p className="text-sm text-stone-600 mb-6 max-w-2xl">Every £50 consultation payment issues one credit. Once a client attends their consultation, the credit auto-applies to their next booking of the exact treatment discussed.</p>

      {loading && <p className="text-sm text-stone-500">Loading…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-sm text-stone-500 py-12 text-center border hairline bg-white">No consultation credits yet.</p>
      )}
      {!loading && rows.length > 0 && (
        <div className="border hairline bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b hairline bg-alt">
              <tr className="mono-label text-left">
                <th className="p-3">Client</th>
                <th className="p-3">Consultation Date</th>
                <th className="p-3">Selected Treatment</th>
                <th className="p-3 text-right">Paid</th>
                <th className="p-3">Status</th>
                <th className="p-3">Reschedule</th>
                <th className="p-3">Redemption</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b hairline last:border-b-0" data-testid={`credit-row-${r.id}`}>
                  <td className="p-3">
                    <div className="font-medium text-stone-900">{r.client_name || "—"}</div>
                    <div className="text-xs text-stone-500">{r.client_email}</div>
                  </td>
                  <td className="p-3">{r.consultation_date} · {r.consultation_time}</td>
                  <td className="p-3">{r.target_treatment_label}</td>
                  <td className="p-3 text-right">{gbp2(r.amount || 50)}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-1 border text-xs ${STATUS_STYLES[r.status] || "bg-stone-100 text-stone-600 border-stone-300"}`} data-testid={`credit-status-${r.id}`}>{STATUS_LABELS[r.status] || r.status.toUpperCase()}</span>
                  </td>
                  <td className="p-3 text-xs">{r.reschedule_used ? (r.extra_reschedule_granted ? "Used · +1 granted" : "Used") : "—"}</td>
                  <td className="p-3 text-xs">
                    {r.redemption_reference ? (
                      <>
                        <div className="font-medium">{r.redemption_reference}</div>
                        <div className="text-stone-500">{(r.redeemed_at || "").slice(0, 10)}</div>
                      </>
                    ) : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <button data-testid={`credit-open-${r.id}`} onClick={() => setOpenCredit(r)} className="mono-label text-stone-700 hover:text-stone-900 underline decoration-dotted underline-offset-4">Manage</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openCredit && <CreditOverrideDrawer credit={openCredit} onClose={() => setOpenCredit(null)} onSaved={() => { setOpenCredit(null); load(); }} />}
    </div>
  );
}

function CreditOverrideDrawer({ credit, onClose, onSaved }) {
  const [action, setAction] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const ACTIONS = [
    { key: "refund", label: "Refund the £50 (Stripe)", danger: true },
    { key: "reinstate", label: "Reinstate credit → Active" },
    { key: "void", label: "Void credit" },
    { key: "allow_extra_reschedule", label: "Allow one extra reschedule" },
    { key: "mark_redeemed", label: "Mark as Redeemed (manual)" },
  ];
  const save = async () => {
    if (!action) { toast.error("Choose an action"); return; }
    if (!note.trim()) { toast.error("Please add an internal note"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/consultation-credits/${credit.id}/override`, { action, note: note.trim() });
      toast.success("Override applied");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Override failed"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm" onClick={onClose} data-testid="credit-override-drawer">
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-[#FAF9F6] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b hairline flex justify-between items-center">
          <span className="mono-label">Consultation Credit · Override</span>
          <button onClick={onClose} data-testid="credit-drawer-close" className="text-stone-500 hover:text-stone-900">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="border hairline bg-white p-4 space-y-1">
            <p className="font-serif text-2xl">{credit.client_name || "—"}</p>
            <p className="text-sm text-stone-600">{credit.client_email}</p>
            <p className="mono-label mt-2">Selected treatment</p>
            <p className="text-sm text-stone-900">{credit.target_treatment_label}</p>
            <p className="mono-label mt-2">Consultation</p>
            <p className="text-sm text-stone-900">{credit.consultation_date} · {credit.consultation_time} · {credit.consultation_reference}</p>
            <p className="mono-label mt-2">Current status</p>
            <p className="text-sm text-stone-900">{STATUS_LABELS[credit.status] || credit.status.toUpperCase()}</p>
          </div>

          <div>
            <p className="mono-label mb-2">Choose action</p>
            <div className="space-y-2">
              {ACTIONS.map((a) => (
                <label key={a.key} className={`flex items-center gap-2 border hairline p-3 cursor-pointer bg-white ${action === a.key ? "border-stone-900" : ""}`}>
                  <input type="radio" data-testid={`override-action-${a.key}`} value={a.key} checked={action === a.key} onChange={() => setAction(a.key)} />
                  <span className={`text-sm ${a.danger ? "text-red-700" : "text-stone-900"}`}>{a.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="mono-label mb-2">Internal note (required)</p>
            <textarea data-testid="override-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full border hairline bg-white p-3 text-sm focus:outline-none focus:border-stone-900" placeholder="Reason for override — captured in the audit log." />
          </div>

          <button data-testid="override-save" onClick={save} disabled={saving || !action || !note.trim()} className="w-full bg-stone-900 text-white py-3 mono-label disabled:opacity-30 hover:bg-[#C5A059] transition" style={{ color: "#fff" }}>{saving ? "Saving…" : "Apply override"}</button>

          {credit.audit?.length > 0 && (
            <div className="pt-4 border-t hairline">
              <p className="mono-label mb-2">Audit log</p>
              <ul className="space-y-2 text-xs">
                {credit.audit.slice().reverse().map((a, i) => (
                  <li key={i} className="border-l-2 border-stone-300 pl-3">
                    <div className="text-stone-900">{a.action}</div>
                    <div className="text-stone-500">{a.at} · {a.by}</div>
                    {a.note && <div className="text-stone-700 mt-1">{a.note}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
